// Top-level at-rule scanner used by compile-time import resolution.
// It parses only declaration at-rules ending with `;`.
import type { TopLevelAtRule } from './types'

// Resolve one top-level declaration at-rule starting at `@` index.
export function parseTopLevelAtRuleDeclarationAt(
  source: string,
  atIndex: number,
  lineStart: number,
): TopLevelAtRule | null {
  let index = atIndex + 1
  let quote: "'" | '"' | null = null
  let depthParen = 0
  let depthBracket = 0
  let line = lineStart

  // Build declaration scan until top-level `;` or abort on block opener `{`.
  while (index < source.length) {
    const char = source[index]
    const previous = index > atIndex ? source[index - 1] : ''

    // Check line number while scanning the at-rule declaration.
    if (char === '\n') {
      line += 1
    } else {
      // Keep line counter unchanged on non-newline chars.
    }

    // Skip quoted sequences opaque until quote is closed.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue inside quote content.
      }

      index += 1
      continue
    } else {
      // Continue structural scanning outside quotes.
    }

    // Start quote mode when scanner meets `'` or `"` tokens.
    if (char === "'" || char === '"') {
      quote = char
      index += 1
      continue
    } else {
      // Keep scanning non-quoted declaration body.
    }

    // Build function/list scope depth so inner `;` is ignored correctly.
    if (char === '(') {
      depthParen += 1
    } else if (char === ')' && depthParen > 0) {
      depthParen -= 1
    } else if (char === '[') {
      depthBracket += 1
    } else if (char === ']' && depthBracket > 0) {
      depthBracket -= 1
    } else {
      // Keep scope counters unchanged for plain chars.
    }

    // Abort parsing when at-rule opens a block instead of declaration.
    if (char === '{' && depthParen === 0 && depthBracket === 0) {
      // Return null when block opener ends declaration scan.
      return null
    } else {
      // Continue declaration parsing for non-block tokens.
    }

    // Finalize declaration token on top-level semicolon.
    if (char === ';' && depthParen === 0 && depthBracket === 0) {
      const statement = source.slice(atIndex, index + 1)
      const name = parseAtRuleName(statement)

      // Reject malformed at-rules without a name.
      if (!name) {
        // Return null for at-rule with empty name.
        return null
      } else {
        // Return declaration token when name is valid.
        return {
          name,
          statement,
          start: atIndex,
          end: index + 1,
          lineStart,
          lineEnd: line,
        }
      }
    } else {
      // Continue scanning until declaration terminator.
    }

    index += 1
  }

  // Return null for unterminated declaration statements.
  return null
}

// Parse at-rule name from statement text (e.g. `@import` -> `import`).
function parseAtRuleName(statement: string): string {
  let index = 1

  // Skip leading spaces after `@`.
  while (index < statement.length && isWhitespace(statement[index])) {
    index += 1
  }

  const start = index
  // Capture name chars (`a-z`, `A-Z`, digits, dash, underscore).
  while (index < statement.length && isAtRuleNameChar(statement[index])) {
    index += 1
  }

  // Return normalized lowercase name for routing checks.
  return statement.slice(start, index).toLowerCase()
}

// Check if character is whitespace.
function isWhitespace(char: string): boolean {
  // Return true when char is a recognized whitespace character.
  return char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f'
}

// Check if character can be part of at-rule names.
function isAtRuleNameChar(char: string): boolean {
  const code = char.charCodeAt(0)
  // Allow ascii letters.
  if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
    // Return true for letter character codes.
    return true
  } else if (code >= 48 && code <= 57) {
    // Allow digits in names after prefix.
    return true
  } else {
    // Allow `_` and `-` separators in names.
    return char === '_' || char === '-'
  }
}

